export const money = value => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(value || 0);
export const number = value => value == null ? '—' : new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);
// Plain en-IN grouped amount (no currency symbol), e.g. 12,34,567.5 — for "{currency} {amount}" labels.
export const amount = (value, digits = 2) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: digits }).format(Number(value) || 0);
export const percent = value => value == null ? '—' : `${value.toFixed(1)}%`;
export const compactMoney = value => {
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return money(value);
};
export const indiaToday = (now = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
export const shiftDay = (day, offset) => { const date = new Date(`${day}T12:00:00Z`); date.setUTCDate(date.getUTCDate() + offset); return date.toISOString().slice(0, 10); };
export const salesDateRange = (period = 'yesterday', now = new Date()) => {
  const end = shiftDay(indiaToday(now), -1);
  if (period === 'mtd') return { date_from: `${end.slice(0, 7)}-01`, date_to: end };
  if (period === 'ytd') return { date_from: `${end.slice(0, 4)}-01-01`, date_to: end };
  if (period === 'yesterday') return { date_from: end, date_to: end };
  const start = shiftDay(end, 1 - Number(period));
  return { date_from: start, date_to: end };
};
// India calendar range that ends today (IST) and includes it, e.g. for live "Last 30 Days" views
// where rows added today must show. period: number of days ('7', '30', ...), 'mtd' or 'ytd'.
export const indiaRangeToToday = (period, now = new Date()) => {
  const end = indiaToday(now);
  if (period === 'mtd') return { date_from: `${end.slice(0, 7)}-01`, date_to: end };
  if (period === 'ytd') return { date_from: `${end.slice(0, 4)}-01-01`, date_to: end };
  return { date_from: shiftDay(end, 1 - Number(period)), date_to: end };
};
export const downloadCsv = (rows, filename) => {
  const cell = value => {
    const text = typeof value === 'string' && /^[=+@\-\t\r]/.test(value) ? `'${value}` : String(value ?? '');
    return `"${text.replace(/"/g, '""')}"`;
  };
  const url = URL.createObjectURL(new Blob(['\ufeff', rows.map(row => row.map(cell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = filename; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
/* ------------------------------------------------------------------------------------------------
 * Sales-page helpers (added for the scope/compare building blocks). Existing exports above are
 * unchanged so older callers keep their behaviour.
 *
 *   wholeMoney(484000)            -> "₹4,84,000"          (no paise; summary numbers)
 *   shortMoney(484000)            -> "₹4.84 L"            shortMoney(28400) -> "₹28 K", 1.2e7 -> "₹1.2 Cr"
 *   changeParts(cur, prev)        -> { delta, pct, isNew, direction: 'up'|'down'|'flat'|'none' }
 *   signedPct(6.2)                -> "+6.2%"
 *   dayCount(from, to)            -> inclusive day count of an India calendar range
 *   shiftYear(day, -1)            -> same date last year (29 Feb -> 28 Feb)
 *   comparisonRange(from, to, c)  -> { date_from, date_to } for compare 'previous' | 'last_year'
 *   formatDateRange(from, to)     -> "1–30 Oct", "15 Sep – 14 Oct", "28 Oct"
 *   periodLabel(from, to, c)      -> "1–30 Oct vs 1–30 Sep" (adds years when they differ)
 *   compareWords(c, days)         -> "vs previous 30 days" | "vs same period last year"
 *   coverTone(days)               -> 'danger' (<15) | 'warning' (<30) | 'success' | 'neutral' (null)
 * ---------------------------------------------------------------------------------------------- */
const trimZeros = text => text.replace(/\.0+$|(\.\d*?)0+$/, '$1');
export const wholeMoney = value => value == null || Number.isNaN(Number(value)) ? '—'
  : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value));
export const shortMoney = value => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Number(value);
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  // Round inside the unit first, then move up a unit when rounding reaches its ceiling
  // (99,999 -> ₹1 L, not ₹100 K; 999.6 -> ₹1 K, not ₹1000).
  const units = [[1e7, 'Cr', Infinity], [1e5, 'L', 100], [1e3, 'K', 100], [1, '', 1000]];
  const decimals = (scale, scaled) => (scale === 1e7 ? (scaled >= 100 ? 0 : 2)
    : scale === 1e5 ? 2 : scale === 1e3 ? (scaled >= 10 ? 0 : 1) : 0);
  let i = units.findIndex(([scale]) => abs >= scale);
  if (i < 0) i = units.length - 1;
  for (; i >= 0; i -= 1) {
    const [scale, suffix, ceiling] = units[i];
    const scaled = abs / scale;
    const rounded = Number(scaled.toFixed(decimals(scale, scaled)));
    if (rounded < ceiling || i === 0) {
      const text = suffix ? `${trimZeros(rounded.toFixed(decimals(scale, rounded)))} ${suffix}` : String(rounded);
      return `${sign}₹${text}`;
    }
  }
  return `${sign}₹${Math.round(abs)}`;
};
export const changeParts = (current, previous) => {
  if (current == null || previous == null || Number.isNaN(Number(current)) || Number.isNaN(Number(previous))) {
    return { delta: null, pct: null, isNew: false, direction: 'none' };
  }
  const cur = Number(current);
  const prev = Number(previous);
  const delta = cur - prev;
  return {
    delta,
    pct: prev ? (delta / Math.abs(prev)) * 100 : null,
    isNew: prev === 0 && cur > 0,
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
  };
};
export const signedPct = pct => {
  if (pct == null || Number.isNaN(Number(pct))) return '';
  const abs = Math.abs(pct);
  if (abs < 0.05) return '0.0%'; // would round to a signed zero ("+0.0%")
  return `${pct > 0 ? '+' : '−'}${abs.toFixed(abs >= 99.95 ? 0 : 1)}%`;
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayParts = day => ({ y: Number(day.slice(0, 4)), m: Number(day.slice(5, 7)), d: Number(day.slice(8, 10)) });
export const isIsoDay = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && !Number.isNaN(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
export const dayCount = (from, to) => Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 864e5) + 1;
export const shiftYear = (day, years) => {
  const { y, m, d } = dayParts(day);
  const target = y + years;
  const last = new Date(Date.UTC(target, m, 0)).getUTCDate();
  return `${target}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
};
export const comparisonRange = (from, to, compare = 'previous') => {
  if (!from || !to) return { date_from: null, date_to: null };
  if (compare === 'last_year') return { date_from: shiftYear(from, -1), date_to: shiftYear(to, -1) };
  const prevTo = shiftDay(from, -1);
  return { date_from: shiftDay(prevTo, 1 - dayCount(from, to)), date_to: prevTo };
};
export const formatDateRange = (from, to, withYear = false) => {
  if (!from || !to) return '';
  const a = dayParts(from);
  const b = dayParts(to);
  const year = withYear ? ` ${b.y}` : '';
  if (from === to) return `${a.d} ${MONTHS[a.m - 1]}${year}`;
  if (a.y !== b.y) return `${a.d} ${MONTHS[a.m - 1]} ${a.y} – ${b.d} ${MONTHS[b.m - 1]} ${b.y}`;
  if (a.m === b.m) return `${a.d}–${b.d} ${MONTHS[a.m - 1]}${year}`;
  return `${a.d} ${MONTHS[a.m - 1]} – ${b.d} ${MONTHS[b.m - 1]}${year}`;
};
export const periodLabel = (from, to, compare = 'previous', previous = null) => {
  if (!from || !to) return '';
  const prev = previous?.date_from && previous?.date_to ? previous : comparisonRange(from, to, compare);
  const withYear = from.slice(0, 4) !== prev.date_from.slice(0, 4) || to.slice(0, 4) !== prev.date_to.slice(0, 4);
  return `${formatDateRange(from, to, withYear)} vs ${formatDateRange(prev.date_from, prev.date_to, withYear)}`;
};
export const compareWords = (compare = 'previous', days = 1) => {
  if (compare === 'last_year') return 'vs same period last year';
  return days === 1 ? 'vs previous day' : `vs previous ${days} days`;
};
export const coverTone = days => days == null ? 'neutral' : days < 15 ? 'danger' : days < 30 ? 'warning' : 'success';
/* ------------------------------------------------------------------------------------------------
 * displayName(value) -> the label for a brand / category / subcategory, mirroring the server's canonical
 * spelling (backend/services/catalog_names.py): NFKC, trimmed, spaces collapsed, Title Case with known
 * acronyms (D2C, USB, LED, BPA, GST, MRP, UK, US, FC, PO) kept, small words (and / of / for / with / &)
 * lower unless first, hyphenated parts capitalised, inner capitals (iPhone) kept. Reserved values pass
 * through as their literal: ALL, *, Unknown; blank -> "Unknown". Stored rows are already canonical
 * after the clean-up; this only keeps legacy spellings from showing in labels before it has run.
 * ---------------------------------------------------------------------------------------------- */
const NAME_RESERVED = { all: 'ALL', '*': '*', unknown: 'Unknown' };
const NAME_ACRONYMS = { d2c: 'D2C', usb: 'USB', led: 'LED', bpa: 'BPA', gst: 'GST', mrp: 'MRP', uk: 'UK', us: 'US', fc: 'FC', po: 'PO' };
const NAME_SMALL = new Set(['and', 'of', 'for', 'with', '&']);
const nameWord = (word, first) => {
  if (!word) return word;
  if (word.includes('-') && word.replace(/-/g, '')) return word.split('-').map(part => nameWord(part, true)).join('-');
  const lower = word.toLowerCase();
  if (NAME_ACRONYMS[lower]) return NAME_ACRONYMS[lower];
  if (NAME_SMALL.has(lower) && !first) return lower;
  const letters = word.replace(/[^\p{L}]/gu, '');
  if (letters.length > 1 && /\p{Lu}/u.test(letters.slice(1)) && letters !== letters.toUpperCase()) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};
export const displayName = value => {
  if (value == null) return 'Unknown';
  const text = String(value).normalize('NFKC').trim().replace(/\s+/g, ' ');
  if (!text) return 'Unknown';
  const reserved = NAME_RESERVED[text.toLowerCase()];
  if (reserved) return reserved;
  return text.split(' ').map((word, index) => nameWord(word, index === 0)).join(' ');
};
